import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

const buttonVariants = tv({
  base: "focus-visible:ring-ring inline-flex items-center cursor-pointer justify-center whitespace-nowrap rounded-full font-medium text-white ring-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
  variants: {
    variant: {
      default: "bg-violet hover:bg-violet80",
      secondary:
        "bg-violet-100 font-normal text-violet-900 rounded-sm hover:bg-violet-200",
      error: "bg-red-700 hover:bg-red-500",
      info: "bg-vivid-blue/10 font-normal text-vivid-blue rounded-sm hover:bg-vivid-blue/20",
      success: "bg-green-700 hover:bg-green-500",
      outline:
        "bg-white border border-violet text-violet hover:bg-violet hover:text-white text-violet",
      "outline-hub":
        "bg-white hover:bg-neutral-100/50 border border-violet text-violet",
      ghost: "bg-transparent",
    },
    size: {
      default: "px-6 py-4 text-base",
      sm: "px-4 py-2 text-sm",
      icon: "h-10 w-10",
    },
    disabled: {
      true: "pointer-events-none bg-[#8080B2]",
    },
    ariaDisabled: {
      true: "bg-blueGrey hover:bg-blueGrey",
    },
  },
  compoundVariants: [
    {
      variant: "outline",
      disabled: true,
      class: "border-blueGrey text-white",
    },
  ],
  defaultVariants: {
    variant: "default",
    size: "default",
    disabled: false,
    ariaDisabled: false,
  },
});

export type ButtonProps = {
  asChild?: boolean;
  isLoading?: boolean;
} & React.ComponentProps<"button"> &
  Omit<VariantProps<typeof buttonVariants>, "ariaDisabled">;

const Button = React.forwardRef<React.ComponentRef<"button">, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      disabled,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const ariaDisabled =
      props["aria-disabled"] === true || props["aria-disabled"] === "true";

    return (
      <Comp
        className={buttonVariants({
          variant,
          size,
          disabled,
          ariaDisabled,
          className,
        })}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="relative flex w-full items-center justify-center">
            <span className="absolute left-0 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {children}
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
